import { type QueryClient } from '@tanstack/react-query';
import { type LoaderFunctionArgs, type RouteObject } from 'react-router-dom';

import { itemQuery } from '@/api/items/getItem';
import { getItems, type ItemsFilters } from '@/api/items/getItems';
import { itemsQueryKey } from '@/api/items/queryKeys';
import { firstAllowed, requirePermissions } from '@/app/requirePermissions';
import { ensureAppQueryData } from '@/hooks/useAppQuery';
import { readNavigationFilters } from '@/hooks/useFilters/useNavigationFilter';
import { readPage } from '@/hooks/usePageParam';
import { pageSlice, paginatedQueryKey } from '@/hooks/usePaginatedList';
import { type ItemType, itemTypes } from '@/models/item/item';
import { PagePermissions } from '@/models/pagePermissions';

// Bundles have their own read permission; the other Catalog pages ride on items:read.
const permissionsFor = (type: ItemType) =>
  type === 'bundle'
    ? PagePermissions.catalogBundles
    : PagePermissions.catalogItems;

export const ITEMS_LIMIT = 48;

/**
 * Starts what the page reads on its first render — its list page and a linked
 * ?show= item — once the gate has passed, instead of after the lazy page
 * renders. Not awaited: the page and sheet show their own loading state.
 */
const warmItemsPage =
  (queryClient: QueryClient, type: ItemType) =>
  ({ params, request }: LoaderFunctionArgs) => {
    const scope = {
      studioCode: params.studioCode ?? '',
      gameCode: params.gameCode ?? '',
      sandboxCode: params.sandboxCode ?? '',
    };
    const { searchParams } = new URL(request.url);

    const show = searchParams.get('show');
    if (show)
      void ensureAppQueryData(queryClient, scope, itemQuery(show, true)).catch(
        () => undefined,
      );

    // The same helpers items.tsx reads through, so both build one key.
    const filters = readNavigationFilters<ItemsFilters>(searchParams);
    const slice = pageSlice(readPage(searchParams), ITEMS_LIMIT);
    void ensureAppQueryData(queryClient, scope, {
      queryKey: paginatedQueryKey(
        itemsQueryKey.paginated(filters, type),
        slice,
      ),
      queryFn: () => getItems({ ...filters, type }, slice.offset, slice.limit),
    }).catch(() => undefined);

    return { type };
  };

export const itemRoute = (queryClient: QueryClient): RouteObject => ({
  path: 'items',
  children: [
    {
      path: '',
      // 'item' and 'currency' share items:read, so Bundles is the only other candidate.
      loader: firstAllowed([
        { to: 'item', oneOf: PagePermissions.catalogItems },
        { to: 'bundle', oneOf: PagePermissions.catalogBundles },
      ]),
    },
    // season_pass is managed under its own /season-passes route, not as an item.
    ...itemTypes
      .filter((type) => type !== 'season_pass')
      .map(
        (type: ItemType): RouteObject => ({
          path: type,
          loader: requirePermissions(
            { oneOf: permissionsFor(type) },
            warmItemsPage(queryClient, type),
          ),
          lazy: () => import('./items'),
        }),
      ),
  ],
});
